import React, { useMemo } from 'react'
import ReactModal from 'react-modal'
import { ReactComponent as CloseModalIcon } from '../../../assets/svg/CloseModalIcon.svg'
import '../../../styles/modals/baseModal.css'

/**
 * Centred card modal.
 *
 * Object-form class names mean react-modal adds none of its default
 * `ReactModal__*` classes, so the app-wide overlay transform/transition rules in
 * styles/modals/TargetGroupModal.css cannot reach this modal. Motion is scoped in
 * styles/modals/baseModal.css (bm-*); ANIMATION_TIMEOUT must cover the longest
 * transition there.
 */
export const ANIMATION_TIMEOUT = 220

const OVERLAY_CLASS = { base: 'bm-overlay', afterOpen: 'bm-overlay--open', beforeClose: 'bm-overlay--closing' }
const CONTENT_CLASS = { base: 'bm-content', afterOpen: 'bm-content--open', beforeClose: 'bm-content--closing' }
const SCROLL_LOCK_CLASS = 'bm-scroll-lock'

const CLOSE_BUTTON_SIZE = 60
const CLOSE_BUTTON_OFFSET = CLOSE_BUTTON_SIZE / 2
const CLOSE_WRAP_STYLE = { top: -CLOSE_BUTTON_OFFSET, left: `calc(50% - ${CLOSE_BUTTON_OFFSET}px)` }
const CLOSE_BUTTON_STYLE = { width: CLOSE_BUTTON_SIZE, height: CLOSE_BUTTON_SIZE }

function BaseModal({
    isOpen,
    onClose,
    onAfterClose,
    children,
    width = '100%',
    maxWidth = 654,
    // No `= {}` default: a fresh literal on every render would defeat the memo below.
    contentStyle,
    overlayStyle,
    showClose = true,
    closeTimeoutMS = ANIMATION_TIMEOUT,
    ariaLabel,
    zIndex = 100,
    backgroundColor = '#fafaff',
}) {
    const style = useMemo(
        () => ({
            overlay: { zIndex, ...overlayStyle },
            content: { width, maxWidth, ...contentStyle },
        }),
        [zIndex, overlayStyle, width, maxWidth, contentStyle]
    )
    const cardStyle = useMemo(() => ({ backgroundColor }), [backgroundColor])

    return (
        <ReactModal
            isOpen={isOpen}
            onRequestClose={onClose}
            onAfterClose={onAfterClose}
            closeTimeoutMS={closeTimeoutMS}
            className={CONTENT_CLASS}
            overlayClassName={OVERLAY_CLASS}
            bodyOpenClassName={SCROLL_LOCK_CLASS}
            htmlOpenClassName={SCROLL_LOCK_CLASS}
            contentLabel={ariaLabel}
            style={style}
        >
            <div className="relative w-full min-h-20 rounded-[10px]" style={cardStyle}>
                {showClose && (
                    <div className="absolute" style={CLOSE_WRAP_STYLE}>
                        <div
                            style={CLOSE_BUTTON_STYLE}
                            className="rounded-full bg-deepPurple hover:bg-darkenPurple cursor-pointer flex items-center justify-center"
                            onClick={onClose}
                        >
                            <CloseModalIcon />
                        </div>
                    </div>
                )}

                {children}
            </div>
        </ReactModal>
    )
}

export default BaseModal
